import { useTranslation } from 'react-i18next'

import { FilterMatchMode, FilterOperator, PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTable, DataTableFilterMeta } from 'primereact/datatable'
import { InputSwitch } from 'primereact/inputswitch'
import { Message } from 'primereact/message'

import {
  BooleanBodyTemplate,
  BooleanFilterTemplate,
  DecimalBodyTemplate,
  DecimalFilterTemplate,
  PackagingIdentifierBodyTemplate,
  PackagingTypeBodyTemplate,
  PropertyIdentifierBodyTemplate,
} from '../../../../../../../../components/tableTemplates'
import { DeliveryForecastDespatchAdvicePackagingMaterial } from '../../../../../../../../data/models'
import { HandleableError } from '../../../../../../../../utils/errors/isXiaError'
import { LoadingScreen } from './common/LoadingScreen'

type VerifyPanelProps = {
  data?: DeliveryForecastDespatchAdvicePackagingMaterial[]
  deleteExistingPackages: boolean
  setDeleteExistingPackages: (value: boolean) => void
  onNext: () => void
  onPrev: () => void
  isLoading: boolean
  error?: HandleableError
}

const defaultFilters: DataTableFilterMeta = {
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  PackagingDataId: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  Description: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
  },
  PackagingMaterial: { value: null, matchMode: FilterMatchMode.EQUALS },
  'TareWeight.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Measurements.Length': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Measurements.Width': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Measurements.Height': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  'Volume.Value': {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  PackagingIdentifier: {
    operator: FilterOperator.AND,
    constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
  },
  PropertyIdentifier: { value: null, matchMode: FilterMatchMode.EQUALS },
  IsStackable: { value: null, matchMode: FilterMatchMode.EQUALS },
}

export const VerifyPanel: React.FC<VerifyPanelProps> = ({
  data,
  deleteExistingPackages,
  setDeleteExistingPackages,
  onNext,
  onPrev,
  isLoading = false,
  error,
}) => {
  const { t } = useTranslation()

  const Footer = () => {
    return (
      <>
        <div className="flex pt-4 justify-content-between">
          <Button
            label={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.footer.previous',
            )}
            icon={PrimeIcons.ARROW_LEFT}
            onClick={() => onPrev()}
            disabled={isLoading}
          />
          <Button
            label={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.footer.next',
            )}
            icon={PrimeIcons.CHECK}
            onClick={() => onNext()}
            disabled={isLoading || !!error}
          />
        </div>
      </>
    )
  }

  if (isLoading)
    return (
      <>
        <LoadingScreen />
        <Footer />
      </>
    )

  if (error)
    return (
      <>
        <Message
          severity="error"
          className="w-full justify-content-start"
          text={error.message}
        />
        <Footer />
      </>
    )

  return (
    <>
      <div className="flex flex-column gap-4">
        <DataTable
          value={data}
          paginator
          rowsPerPageOptions={[10, 20, 50]}
          rows={10}
          scrollable
          scrollHeight="50vh"
          filters={defaultFilters}
        >
          <Column
            field="PackagingDataId"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.PackagingDataId',
            )}
            sortable
            filter
          />
          <Column
            field="Description"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.Description',
            )}
            sortable
            filter
          />
          <Column
            field="PackagingMaterial"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.PackagingMaterial',
            )}
            sortable
            filter
            body={(rowData: DeliveryForecastDespatchAdvicePackagingMaterial) =>
              PackagingTypeBodyTemplate(rowData.PackagingMaterial)
            }
          />
          <Column
            field="Measurements.Length"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.MeasurementsLength',
            )}
            sortable
            filter
            filterElement={DecimalFilterTemplate}
            dataType="numeric"
            body={(rowData: DeliveryForecastDespatchAdvicePackagingMaterial) =>
              DecimalBodyTemplate(rowData.Measurements?.Length, 2)
            }
          />
          <Column
            field="Measurements.Width"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.MeasurementsWidth',
            )}
            sortable
            filter
            filterElement={DecimalFilterTemplate}
            dataType="numeric"
            body={(rowData: DeliveryForecastDespatchAdvicePackagingMaterial) =>
              DecimalBodyTemplate(rowData.Measurements?.Width, 2)
            }
          />
          <Column
            field="Measurements.Height"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.MeasurementsHeight',
            )}
            sortable
            filter
            filterElement={DecimalFilterTemplate}
            dataType="numeric"
            body={(rowData: DeliveryForecastDespatchAdvicePackagingMaterial) =>
              DecimalBodyTemplate(rowData.Measurements?.Height, 2)
            }
          />
          <Column
            field="TareWeight.Value"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.TareWeightValue',
            )}
            sortable
            filter
            filterElement={DecimalFilterTemplate}
            dataType="numeric"
            body={(rowData: DeliveryForecastDespatchAdvicePackagingMaterial) =>
              DecimalBodyTemplate(rowData.TareWeight?.Value)
            }
          />
          <Column
            field="Volume.Value"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.VolumeValue',
            )}
            sortable
            filter
            filterElement={DecimalFilterTemplate}
            dataType="numeric"
            body={(rowData: DeliveryForecastDespatchAdvicePackagingMaterial) =>
              DecimalBodyTemplate(rowData.Volume?.Value)
            }
          />
          <Column
            field="PackagingIdentifier"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.PackagingIdentifier',
            )}
            sortable
            filter
            body={(rowData: DeliveryForecastDespatchAdvicePackagingMaterial) =>
              PackagingIdentifierBodyTemplate(rowData.PackagingIdentifier)
            }
          />
          <Column
            field="PropertyIdentifier"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.PropertyIdentifier',
            )}
            sortable
            filter
            body={(rowData: DeliveryForecastDespatchAdvicePackagingMaterial) =>
              PropertyIdentifierBodyTemplate(rowData.PropertyIdentifier)
            }
          />
          <Column
            field="IsStackable"
            header={t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.table.columns.IsStackable',
            )}
            sortable
            filter
            filterElement={BooleanFilterTemplate}
            dataType="boolean"
            body={(rowData: DeliveryForecastDespatchAdvicePackagingMaterial) =>
              BooleanBodyTemplate(rowData.IsStackable)
            }
          />
        </DataTable>
        <div className="flex gap-2">
          <InputSwitch
            id="delete"
            checked={deleteExistingPackages}
            onChange={e => setDeleteExistingPackages(e.value)}
          />
          <label htmlFor="delete">
            {t(
              'masterDataManagement.packagingData.table.upload.dialog.verifyPanel.deleteSwitch',
            )}
          </label>
        </div>
      </div>
      <Footer />
    </>
  )
}
